// Shared helpers and constants for the Rule Builder + Rule Editor Drawer.
//
// Lives in its own file to avoid a circular import (RuleEditorDrawer needs
// `defaultProvenance`, `ChipList`, etc., and RuleBuilder needs to render
// the drawer and pass it data). Both files import from here.
import { useState } from 'react';
import { ShieldCheck, BookOpen, FlaskConical, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

// ─── Static option lists ───────────────────────────────────────────────
export const EARN_TYPES = [
  { value: 'rate_per_spend', label: 'Rate per spend (e.g. 5pt / ₹150)' },
  { value: 'percent_cashback', label: 'Percent cashback (e.g. 5%)' },
  { value: 'milestone_bonus', label: 'Milestone bonus' },
];
export const PERIODS = ['daily', 'monthly', 'quarterly', 'yearly', 'lifetime'];
export const MODES = [
  { value: 'any', label: 'Online & offline' },
  { value: 'online', label: 'Online only' },
  { value: 'offline', label: 'Offline only' },
];

export const CONFIDENCE_LEVELS = [
  { value: 'official',     label: 'Official',        tone: 'emerald', icon: ShieldCheck,    hint: 'Issuer-documented \u2014 from bank T&Cs or app' },
  { value: 'verified',     label: 'Verified',        tone: 'teal',    icon: BookOpen,       hint: 'Confirmed by support / multiple community reports' },
  { value: 'experimental', label: 'Experimental',    tone: 'amber',   icon: FlaskConical,   hint: 'Internal experiment / staging tests' },
  { value: 'low',          label: 'Low confidence',  tone: 'rose',    icon: AlertTriangle,  hint: 'Anecdotal \u2014 needs verification' },
];

export const SOURCE_TYPES = [
  { value: 'issuer_docs', label: 'Issuer docs' },
  { value: 'support_confirmed', label: 'Support confirmed' },
  { value: 'community_observed', label: 'Community observed' },
  { value: 'experiment', label: 'Internal experiment' },
  { value: 'reverse_engineered', label: 'Reverse-engineered' },
];

export const VERIFICATION_STATUSES = [
  { value: 'verified', label: 'Verified' },
  { value: 'pending', label: 'Pending' },
  { value: 'unverified', label: 'Unverified' },
  { value: 'disputed', label: 'Disputed' },
];

const TONE_CLASS = {
  emerald: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  teal:    'bg-primary/10 text-primary border-primary/30',
  amber:   'bg-amber-500/10 text-amber-600 border-amber-500/30',
  rose:    'bg-rose-500/10 text-rose-600 border-rose-500/30',
};


// ─── Defaults / factories ──────────────────────────────────────────────
export const defaultProvenance = () => ({
  confidence_level: 'official',
  source_type: 'issuer_docs',
  verification_status: 'verified',
  community_verified: false,
  observation_notes: '',
  source_url: '',
  last_verified_at: null,
});

export const genRuleId = () => `rule-${Math.random().toString(16).slice(2, 10)}`;
export const genPoolId = () => `pool-${Math.random().toString(16).slice(2, 10)}`;
export const genTierId = () => `tier-${Math.random().toString(16).slice(2, 10)}`;
export const genMileId = () => `mile-${Math.random().toString(16).slice(2, 8)}`;

export const emptyRule = (cardDefaults = {}) => ({
  id: genRuleId(),
  label: 'New rule',
  priority: 10,
  exclude: false,
  match: { categories: [], mcc: [], merchants: [], mode: 'any', international: null, contexts: [] },
  earn: {
    type: 'rate_per_spend',
    currency: cardDefaults.currency || 'RP',
    rate: 1,
    per: 100,
    point_value_inr: cardDefaults.point_value_inr != null ? Number(cardDefaults.point_value_inr) : 1,
  },
  cap: null,
  notes: '',
  provenance: defaultProvenance(),
});

export const emptyMilestone = () => ({
  id: genMileId(),
  name: 'Anniversary bonus',
  target_inr: 500000,
  reward: '5,000 points',
  reward_value_inr: 5000,
  period: 'anniversary',
  provenance: defaultProvenance(),
});

export const emptyCapPool = () => ({
  id: genPoolId(),
  label: 'New shared cap pool',
  amount_inr: null,
  reward_inr: null,
  reward_points: 18000,
  period: 'monthly',
  spillover_mode: 'fallback_rule',
  notes: '',
  provenance: defaultProvenance(),
});

export const emptyTier = () => ({
  id: genTierId(),
  name: 'New tier',
  threshold_inr: 500000,
  reward_value_inr: 0,
  rate_multiplier: 1.0,
  benefits: [],
  period: 'yearly',
  notes: '',
  provenance: defaultProvenance(),
});


// ─── Cap helpers ───────────────────────────────────────────────────────
export const CAP_KINDS = ['amount_inr', 'reward_inr', 'reward_points'];

export function capKind(cap) {
  if (!cap) return 'amount_inr';
  for (const k of CAP_KINDS) {
    if (cap[k] !== undefined && cap[k] !== null) return k;
  }
  return 'amount_inr';
}

export function setCapKind(cap, nextKind, setCap) {
  const currentKind = capKind(cap);
  const currentVal = cap?.[currentKind] ?? null;
  const patch = { amount_inr: null, reward_inr: null, reward_points: null };
  patch[nextKind] = currentVal != null ? currentVal : (nextKind === 'reward_points' ? 50000 : 100000);
  setCap(patch);
}


// ─── Pool helpers ──────────────────────────────────────────────────────
export const poolDimension = (pool) => {
  if (!pool) return 'reward_points';
  if (pool.amount_inr != null) return 'amount_inr';
  if (pool.reward_inr != null) return 'reward_inr';
  return 'reward_points';
};
export const poolLimit = (pool) => {
  const d = poolDimension(pool);
  return pool?.[d] ?? null;
};
export const formatPoolLimit = (pool) => {
  const d = poolDimension(pool);
  const v = poolLimit(pool);
  if (v == null) return '\u2014';
  const fmt = (n) => Number(n).toLocaleString('en-IN');
  if (d === 'amount_inr') return `\u20b9${fmt(v)}`;
  if (d === 'reward_inr') return `\u20b9${fmt(v)} reward`;
  return `${fmt(v)} pts`;
};


// ─── Confidence badge component ────────────────────────────────────────
export const ConfidenceBadge = ({ provenance, className }) => {
  const level = provenance?.confidence_level || 'official';
  const meta = CONFIDENCE_LEVELS.find((c) => c.value === level) || CONFIDENCE_LEVELS[0];
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={cn('text-[10px] gap-1 uppercase tracking-wide px-1.5', TONE_CLASS[meta.tone], className)} data-testid={`confidence-badge-${level}`} title={meta.hint}>
      <Icon className="h-3 w-3" /> {meta.label}
    </Badge>
  );
};


// ─── Chip list component (reusable: rule categories / mcc / merchants / tier benefits) ─
export const ChipList = ({ label, items, onChange, placeholder, testid, locked }) => {
  const [draft, setDraft] = useState('');
  const commit = (raw) => {
    const parts = String(raw)
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return;
    const next = [...items];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    onChange(next);
    setDraft('');
  };
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
      <div className="mt-1 flex flex-wrap gap-1.5 items-center rounded-md border border-input bg-background px-2 py-1.5 min-h-9">
        {(items || []).map((it) => (
          <Badge
            key={it}
            variant="outline"
            title={it}
            className="text-[10px] font-mono-num h-6 max-w-[220px] gap-1 pr-1 inline-flex items-center"
            data-testid={`${testid}-chip-${it.slice(0, 32)}`}
          >
            <span className="truncate">{it}</span>
            {!locked && (
              <button onClick={() => onChange(items.filter((x) => x !== it))} className="shrink-0 hover:text-rose-500" aria-label={`Remove ${it}`}>×</button>
            )}
          </Badge>
        ))}
        {!locked && (
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onPaste={(e) => {
              const text = e.clipboardData.getData('text');
              if (/[,;\n]/.test(text)) {
                e.preventDefault();
                commit(text);
              }
            }}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ',') && draft.trim()) {
                e.preventDefault();
                commit(draft);
              }
              if (e.key === 'Backspace' && !draft && items.length) {
                onChange(items.slice(0, -1));
              }
            }}
            onBlur={() => { if (draft.trim()) commit(draft); }}
            className="flex-1 min-w-[120px] bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            data-testid={testid}
          />
        )}
      </div>
    </div>
  );
};
